import { greyMui, redMui, rydeColors } from "@ryde/theme";

/** Live-map course-code pin size (px); keep in sync with MAP_MARKER_ICON_STYLES. */
export const COURSE_CODE_MARKER_WIDTH_PX = 41;
export const COURSE_CODE_MARKER_HEIGHT_PX = 45;

/**
 * Station pin art — one geometry behind both renderers: the Leaflet clip-path in `MAP_MARKER_ICON_STYLES`
 * and the MapLibre SVG pin.
 *
 * A station marker anchors at the pin's tip rather than somewhere inside its body, so the pin hangs above
 * its lat/lng and leaves the station point itself uncovered. Basemap POIs — a bus stop, say — are baked
 * into the raster tile, so an opaque pin drawn over the point is the only thing that can hide them.
 */
export const STATION_PIN_WIDTH_PX = 29;
export const STATION_PIN_HEIGHT_PX = 40;

/** Tip of `STATION_PIN_PATH`, in the pin's own coordinates — every station marker anchors here. */
export const STATION_PIN_TIP_X = 14.2484;
export const STATION_PIN_TIP_Y = 38.8676;

/** Teardrop outline (viewBox `0 0 29 40`), read verbatim as both a CSS `clip-path` and an SVG `path`. */
export const STATION_PIN_PATH =
  "M.94 15.8805.94 15.8803C.9442 11.8885 2.3699 8.0792 4.8778 5.2842 7.3825 2.4929 10.755.945 14.2484.94 17.7417.945 21.1144 2.4929 23.619 5.2842 26.1269 8.0792 27.5526 11.8885 27.5569 15.8803V15.8805C27.561 19.1376 26.6058 22.2968 24.8508 24.8733L24.7688 24.9938 24.7681 24.9964 24.6504 25.1674C24.5984 25.2429 24.5452 25.3198 24.5017 25.3823 24.4799 25.4137 24.4612 25.4403 24.4467 25.461 24.4392 25.4717 24.4339 25.4791 24.4305 25.4838 24.4286 25.4865 24.4273 25.4884 24.4265 25.4895 24.4255 25.4908 24.4253 25.4911 24.4257 25.4905L14.2484 38.8676 4.0706 25.4902 4.0706 25.4902 4.0668 25.4852C4.067 25.4855 4.0664 25.4846 4.0649 25.4826 4.0585 25.4737 4.0367 25.4432 3.9961 25.3849 3.9525 25.3225 3.8993 25.2455 3.8472 25.1697 3.7953 25.0941 3.7453 25.0211 3.7081 24.9668L3.6637 24.9016 3.6514 24.8837 3.6482 24.879 3.6474 24.8779 3.6473 24.8777C1.8914 22.2999.9357 19.1392.94 15.8805Z";

export const MapConst = {
  TRACKING_POINT_RADIUS: 3,
  TRANSPARENT_AREA_ADD: 10,
  DASHED_LINE_OPTIONS: "2,8",
  DASHED_LINE_COLOR: greyMui._600,
  MINUTES_LIMIT_SOLID_LINE: 2,
  /**the actual GPS trail line is drawn at 60% opacity (planned color, dashed) to read as parallel to the solid planned path. */
  GPS_TRAIL_OPACITY: 0.8,
  /** Dash pattern for the GPS trail line — clearly visible dashes (the sparse DASHED_LINE_OPTIONS "2,8" reads as no line). */
  GPS_TRAIL_DASH_OPTIONS: "10,8",
  /**perpendicular screen offset (px) of the GPS trail line so it runs parallel to (not on top of) the planned path. */
  GPS_TRAIL_OFFSET_PX: 8,
  /** A GPS point is treated as "overlapping" the planned path when within this many screen px of it — only those points get offset. */
  GPS_TRAIL_OVERLAP_THRESHOLD_PX: 10,
  /** Shadow/casing drawn beneath the GPS trail line so the dashed, translucent line stays readable on the map. */
  GPS_TRAIL_SHADOW_COLOR: rydeColors.black,
  GPS_TRAIL_SHADOW_OPACITY: 0.3,
  GPS_TRAIL_SHADOW_WEIGHT: 5,
  /** Off-route (detour) styling — the GPS trail line and points turn red where the vehicle left the planned route. */
  GPS_TRAIL_DETOUR_COLOR: redMui._600,
  /** In-detour points are drawn slightly larger with a white border so they stand out from the on-route dots. */
  TRACKING_POINT_DETOUR_RADIUS: 4,
  TRACKING_POINT_DETOUR_BORDER_COLOR: rydeColors.white,
  TRACKING_POINT_DETOUR_BORDER_WEIGHT: 2,
} as const;

/**
 * Leaflet's default path colour. Leaflet-geoman drew every editing affordance in it — the rubber band
 * while a shape is being drawn, and the rim of each vertex handle — independently of the shape's own
 * colour. Kept as a literal rather than mapped onto a ryde token so those tools still look like the ones
 * they replace.
 */
export const MAP_DEFAULT_PATH_COLOR = "#3388ff";

/** Google API session expiry timestamps are padded to 13 ms digits. */
export const MAP_GOOGLE_TIMESTAMP_MS_LENGTH = 13;
/** `[lat, lng]` tuple length for map coordinate validation. */
export const MAP_LAT_LNG_TUPLE_LENGTH = 2;
/** Max cached decoded route polylines (live map autofit + layers). */
export const MAP_ROUTE_POLYLINE_DECODE_CACHE_MAX_ENTRIES = 512;

export const CORRECTING_FIX_BOUNDS_TIMEOUT = 100;
/** Debounce map autofit after route/selection changes and container resize. */
export const MAP_AUTOFIT_DEBOUNCE_MS = 150;

/**
 * Shown until the per-viewport copyright arrives, and kept if the request fails. Google's terms require an
 * attribution whenever their tiles are on screen, so the line must never be empty while they are.
 */
export const GOOGLE_ATTRIBUTION_FALLBACK = "Map data ©Google";

/** Debounce on re-fetching Google's per-viewport copyright line while the map is being panned/zoomed. */
export const GOOGLE_ATTRIBUTION_FETCH_DELAY_MS = 300;
export const MAP_PAD_RATIO = 0.05;
export const MAP_PAD_RATIO_CORRECTING = 0.07;
export const PAD_AROUND_DIMENSION = 50;
/**
 * `fitBounds` applies this padding on both sides of each axis. Max padding is therefore
 * `floor(min(width, height) / FIT_PADDING_AXES) - FIT_PADDING_AXIS_CLEARANCE_PX` so 50px on an 80px-tall
 * ride card (39) still leaves a 1px remainder and never produces NaN zoom.
 */
export const FIT_PADDING_AXIS_CLEARANCE_PX = 1;
export const FIT_PADDING_AXES = 2;
/** Below this difference in degrees (~1.1 m) two bounds describe the same view. */
export const BOUNDS_EQUALITY_EPSILON_DEG = 1e-5;
/** Span (deg) to pad by when the bounds are a single point and the ratio has nothing to scale. */
export const DEGENERATE_BOUNDS_SPAN_DEG = 0.01;

export const AUTOCOMPLETE_COUNTRY_NAMES_EXCLUDED = new Set(["israel", "ישראל"]);
